import { useNavigate, useParams } from "@tanstack/react-router";
import { useMemo, type ReactNode } from "react";
import { VARIABLE_GROUPS } from "@babackapp/variables-registry";
import { BlockCategory } from "@babackapp/editor-blocks-library/blocks";
import defaultEmailsTheme from "@babackapp/blocks-editor/constants/default-emails-theme";
import type { EmailsThemeDto } from "@babackapp/private-api-client/types";
import {
  TemplatesEditorRoutingProvider,
  type TemplatesEditorRouting,
} from "./context/templates-editor-routing-context";
import {
  TemplatesEditorConfigProvider,
  type TemplatesEditorConfig,
} from "./context/templates-editor-config-context";
import { useDefaultEditorVariables } from "./lib/variables";
import useCurrentShopSuspense from "./hooks/use-current-shop-suspense";

/**
 * Tracking host context (track-office / workflows editor): supplies both the
 * routing surface (shopId + navigation, from TanStack Router) and the tracking
 * editor config (tracking variables + groups, domain "tracking").
 *
 * Wrap the tracking templates list / create flow and the standalone preview
 * with this. The editor itself (TemplatesEditor) wraps its own core with it.
 */
export function TrackingTemplatesRoutingProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { shopId } = useParams({ from: "/shops/$shopId" });
  const navigate = useNavigate();

  const routing = useMemo<TemplatesEditorRouting>(
    () => ({
      shopId,
      openTemplate: (templateId) =>
        navigate({
          to: "/shops/$shopId/notifications-templates/$templateId",
          params: { shopId, templateId },
        }),
      openWorkflow: (workflowId) =>
        navigate({
          to: "/shops/$shopId/notifications-workflows/$workflowId",
          params: { shopId, workflowId },
        }),
    }),
    [shopId, navigate],
  );

  return (
    <TemplatesEditorRoutingProvider value={routing}>
      <TrackingTemplatesConfigProvider>
        {children}
      </TrackingTemplatesConfigProvider>
    </TemplatesEditorRoutingProvider>
  );
}

/**
 * Resolves the tracking config inside the routing provider (variable resolution
 * reads the current shop, which needs the routing context).
 */
function TrackingTemplatesConfigProvider({ children }: { children: ReactNode }) {
  const variables = useDefaultEditorVariables();
  const { data: shop } = useCurrentShopSuspense();
  const subjectManagedByWorkflow =
    shop.trackingPreferences?.useTemplatesAndWorkflowsV2 === true;
  const theme = (shop.trackingPreferences?.emailsTheme ||
    defaultEmailsTheme) as Required<EmailsThemeDto>;

  const config = useMemo<TemplatesEditorConfig>(
    () => ({
      domain: "tracking",
      variables,
      groups: VARIABLE_GROUPS,
      theme,
      // Return-only blocks stay exclusive to the return editor.
      hiddenBlockCategories: [BlockCategory.RETURN],
      subjectManagedByWorkflow,
    }),
    [variables, subjectManagedByWorkflow, theme],
  );

  return (
    <TemplatesEditorConfigProvider config={config}>
      {children}
    </TemplatesEditorConfigProvider>
  );
}
